/**
 * Shared action-target rules for the pro-gen / featured game UI.
 *
 * The ActionBar dropdown and the modals that enforce the limits must never
 * disagree, so the round limits and the "is this target used up?" predicates
 * live here and are imported by both sides (ExamineModal imports
 * MAX_EXAMINE_ROUNDS / getExamineFocusIds; ActionBar imports the predicates).
 *
 * ANTI-ORACLE CONTRACT: every predicate below reads ONLY the player's own
 * completed rounds (interrogation dialogues, examination rounds, used
 * focuses). Nothing here may ever branch on killer status, suspect role or
 * any other per-suspect attribute — the dropdown must have identical trigger
 * geometry for every suspect.
 */

import { MAX_INTERROGATION_ROUNDS } from './actionHelpers';

// Maximum number of examination rounds per location (mirrors ExamineModal).
export const MAX_EXAMINE_ROUNDS = 2;

// Focus ids offered per difficulty. Easy leaves out environmental context;
// medium/hard offer all five. ExamineModal builds its focus option objects by
// filtering on these ids so the list and the exhaustion rule cannot drift.
export const BASE_EXAMINE_FOCUS_IDS = [
  'physical_traces',
  'paper_trail',
  'witness_accounts',
  'timeline_patterns',
];

export const ENVIRONMENTAL_EXAMINE_FOCUS_ID = 'environmental_context';

const EXAMINE_FOCUS_IDS_BY_DIFFICULTY = {
  easy: BASE_EXAMINE_FOCUS_IDS,
  medium: [...BASE_EXAMINE_FOCUS_IDS, ENVIRONMENTAL_EXAMINE_FOCUS_ID],
  hard: [...BASE_EXAMINE_FOCUS_IDS, ENVIRONMENTAL_EXAMINE_FOCUS_ID],
};

export const getExamineFocusIds = (difficulty) => (
  EXAMINE_FOCUS_IDS_BY_DIFFICULTY[difficulty] || EXAMINE_FOCUS_IDS_BY_DIFFICULTY.easy
);

/** Rounds the player has already completed at a location. */
export const getExamineRoundsUsed = (examinations, locationId) => {
  const entry = examinations?.[locationId];
  if (Array.isArray(entry)) return entry.length;
  if (Number.isFinite(Number(entry?.rounds_completed))) return Number(entry.rounds_completed);
  return 0;
};

/**
 * A location is used up when the player has spent every examine round there
 * OR has already used every focus the difficulty offers — the same two
 * conditions ExamineModal uses to stop accepting another round.
 */
export const isLocationExamineExhausted = ({
  locationId,
  examinations,
  examinedFocuses,
  difficulty,
} = {}) => {
  if (!locationId) return false;

  if (getExamineRoundsUsed(examinations, locationId) >= MAX_EXAMINE_ROUNDS) {
    return true;
  }

  const focusIds = getExamineFocusIds(difficulty);
  const used = new Set(examinedFocuses?.[locationId] || []);
  return focusIds.every((focusId) => used.has(focusId));
};

/** Interrogation rounds the player has completed with a suspect. */
export const getInterrogationRoundsCompleted = (interrogationDialogues, suspectId) => {
  const dialogues = interrogationDialogues?.[suspectId];
  return Array.isArray(dialogues) ? dialogues.length : 0;
};

/**
 * A suspect drops out of the INTERVIEW list once the player has completed the
 * maximum rounds — the same count InterrogationModal / SuspectWall use to
 * refuse a third round.
 */
export const isSuspectInterviewExhausted = (interrogationDialogues, suspectId) => (
  getInterrogationRoundsCompleted(interrogationDialogues, suspectId) >= MAX_INTERROGATION_ROUNDS
);

// Why an action has no targets. The two states read very differently to a
// player: at turn 0 nothing is discovered yet, later on everything discovered
// has been used up. TRAVEL and READ are never filtered, so they can only ever
// reach the `none_discovered` state.
export const ACTION_EMPTY_HINTS = {
  interview: {
    none_discovered: 'No suspects found yet - investigate to meet them.',
    all_used: 'Every suspect you\'ve found has been fully interviewed.',
  },
  examine: {
    none_discovered: 'No locations discovered yet.',
    all_used: 'You\'ve searched every location you\'ve found.',
  },
  travel: {
    none_discovered: 'No locations discovered yet.',
    all_used: 'No locations discovered yet.',
  },
  read: {
    none_discovered: 'No documents discovered yet.',
    all_used: 'No documents discovered yet.',
  },
};

const DISCOVERED_KEY_FOR_ACTION = {
  interview: 'suspects',
  examine: 'locations',
  travel: 'locations',
  read: 'documents',
};

const discoveredOnly = (items, discoveredIds) => {
  const discovered = new Set(discoveredIds || []);
  return (items || []).filter((item) => item?.id && discovered.has(item.id));
};

/**
 * Targets offered for an action.
 *
 * - interview: discovered suspects with interrogation rounds remaining
 * - examine:   discovered locations with examine rounds/focuses remaining
 * - travel:    every discovered location (never filtered)
 * - read:      every discovered document (never filtered)
 */
export const buildActionTargets = (action, state = {}) => {
  const {
    suspects,
    locations,
    documents,
    discovered,
    interrogationDialogues,
    examinations,
    examinedFocuses,
    selectedDifficulty,
  } = state;

  switch (action) {
    case 'interview':
      return discoveredOnly(suspects, discovered?.suspects)
        .filter((suspect) => !isSuspectInterviewExhausted(interrogationDialogues, suspect.id))
        .map((suspect) => ({ value: suspect.id, label: suspect.name }));
    case 'examine':
      return discoveredOnly(locations, discovered?.locations)
        .filter((location) => !isLocationExamineExhausted({
          locationId: location.id,
          examinations,
          examinedFocuses,
          difficulty: selectedDifficulty,
        }))
        .map((location) => ({ value: location.id, label: location.name }));
    case 'travel':
      return discoveredOnly(locations, discovered?.locations)
        .map((location) => ({ value: location.id, label: location.name }));
    case 'read':
      return discoveredOnly(documents, discovered?.documents)
        .map((document) => ({ value: document.id, label: document.title || document.name }));
    default:
      return [];
  }
};

/** True when an action needs a target but has none left. */
export const isActionExhausted = (action, state = {}) => {
  if (!action || action === 'consult_guide') return false;
  return buildActionTargets(action, state).length === 0;
};

/**
 * `null` when the action still has targets, otherwise which empty state it is
 * in: nothing discovered yet vs everything discovered already used up.
 */
export const getActionEmptyReason = (action, state = {}) => {
  if (!isActionExhausted(action, state)) return null;

  const discoveredKey = DISCOVERED_KEY_FOR_ACTION[action];
  const discoveredCount = (state.discovered?.[discoveredKey] || []).length;
  return discoveredCount === 0 ? 'none_discovered' : 'all_used';
};

/** Player-facing copy for an exhausted action, or `null` when it has targets. */
export const getActionEmptyHint = (action, state = {}) => {
  const reason = getActionEmptyReason(action, state);
  if (!reason) return null;
  return ACTION_EMPTY_HINTS[action]?.[reason] || 'Nothing left to choose here.';
};
